import React, { useState } from 'react';
import Modal from '../ui/Modal';
import { CreateMeetingRequest } from '../../types/meeting';
import '../../styles/meetings.css';

interface CreateMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (request: CreateMeetingRequest) => Promise<void>;
}

const CreateMeetingModal: React.FC<CreateMeetingModalProps> = ({ isOpen, onClose, onCreate }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [location, setLocation] = useState('');
  const [committee, setCommittee] = useState('');
  const [teamsJoinUrl, setTeamsJoinUrl] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const reset = () => {
    setTitle('');
    setDescription('');
    setScheduledAt('');
    setDurationMinutes(60);
    setLocation('');
    setCommittee('');
    setTeamsJoinUrl('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!title.trim()) return;
    setIsCreating(true);
    try {
      await onCreate({
        title: title.trim(),
        description: description.trim() || undefined,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
        duration_minutes: durationMinutes,
        location: location.trim() || undefined,
        committee: committee.trim() || undefined,
        teams_join_url: teamsJoinUrl.trim() || undefined
      });
      reset();
    } catch {
      // Error handling is done in the parent component
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="New Meeting" maxWidth="560px">
      <div className="meeting-form">
        <div className="meeting-form-field">
          <label className="meeting-form-label">Title *</label>
          <input
            className="meeting-form-input"
            type="text"
            placeholder="e.g. Q3 Strategy Review"
            value={title}
            maxLength={80}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
        </div>

        <div className="meeting-form-field">
          <label className="meeting-form-label">Description</label>
          <textarea
            className="meeting-form-textarea"
            placeholder="What is this meeting about?"
            value={description}
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="meeting-form-row">
          <div className="meeting-form-field">
            <label className="meeting-form-label">Date &amp; Time</label>
            <input
              className="meeting-form-input"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
          </div>
          <div className="meeting-form-field">
            <label className="meeting-form-label">Duration (minutes)</label>
            <input
              className="meeting-form-input"
              type="number"
              min={15}
              max={480}
              step={15}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(parseInt(e.target.value, 10) || 60)}
            />
          </div>
        </div>

        <div className="meeting-form-row">
          <div className="meeting-form-field">
            <label className="meeting-form-label">Location</label>
            <input
              className="meeting-form-input"
              type="text"
              placeholder="e.g. Boardroom / Teams"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
          <div className="meeting-form-field">
            <label className="meeting-form-label">Committee</label>
            <input
              className="meeting-form-input"
              type="text"
              placeholder="e.g. Executive Committee"
              value={committee}
              onChange={(e) => setCommittee(e.target.value)}
            />
          </div>
        </div>

        <div className="meeting-form-field">
          <label className="meeting-form-label">Teams meeting link</label>
          <input
            className="meeting-form-input"
            type="url"
            placeholder="https://teams.microsoft.com/l/meetup-join/…"
            value={teamsJoinUrl}
            onChange={(e) => setTeamsJoinUrl(e.target.value)}
          />
        </div>

        <div className="meeting-form-actions">
          <button className="meeting-btn meeting-btn-secondary" onClick={handleClose} disabled={isCreating}>
            Cancel
          </button>
          <button
            className="meeting-btn meeting-btn-primary"
            onClick={handleCreate}
            disabled={!title.trim() || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Meeting'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateMeetingModal;
